import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

import { GetChannelGroups } from './useChannelGroups'

export const CreateChannelGroupMutation = graphql(/* GraphQL */ `
  mutation createChannelGroup($input: CreateChannelGroupInput!) {
    createChannelGroup(input: $input) {
      __typename
      ... on ChannelGroup {
        id
        name
        channels {
          id
          name
        }
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

interface UseCreateChannelGroupReturn {
  createChannelGroup: ({
    name,
    channelIds,
    accountId,
    organizationId,
  }: {
    name: string
    channelIds?: string[]
    accountId: string
    organizationId: string
  }) => Promise<void>
  loading: boolean
}

/**
 * Custom hook to create a channel group.
 * @param setSelectedChannelToRemove The function to update the state of the selected channel to remove.
 * @returns {UseCreateChannelGroupReturn} The function to trigger creating a channel group and the loading state.
 */
export const useCreateChannelGroup = (): UseCreateChannelGroupReturn => {
  const [executeCreateChannelGroup, { loading }] = useMutation(
    CreateChannelGroupMutation,
  )

  const createChannelGroup = useCallback(
    async ({
      accountId,
      organizationId,
      name,
      channelIds,
    }: {
      accountId: string
      organizationId: string
      name: string
      channelIds?: string[]
    }): Promise<void> => {
      if (!accountId || !organizationId) {
        throw new Error('Account ID and organization ID are required')
      }

      try {
        const { data, error } = await executeCreateChannelGroup({
          variables: {
            input: {
              accountId,
              organizationId,
              name,
              channelIds,
            },
          },
          refetchQueries: [GetChannelGroups],
        })

        if (data?.createChannelGroup.__typename === 'ChannelGroup') {
          toast.success('Channel group created successfully!', {})
        }

        if (error) {
          toast.error('Failed to create channel group')
          const unexpectedError = new Error(
            error.message || 'Unexpected API response',
          )
          logError(unexpectedError, { metaData: { data, error } })
        }
      } catch (e) {
        toast.error('Failed to create channel group')
        logError(e instanceof Error ? e : new Error(String(e)), {
          metaData: { variables: { name, channelIds } },
        })
      }
    },
    [executeCreateChannelGroup],
  )

  return {
    createChannelGroup,
    loading,
  }
}
